import {
    Button,
    ClickEvent,
    Label,
    Snackbar,
    TableRow,
    TableRowDisplayEvent,
    Toast
} from "@mcleod/components";
import { Alignment, Api, ModelRow, StringUtil } from "@mcleod/core";
import { Movement, Orders } from "@mcleod/dispatch";
import { AutogenLayoutCarrierDriverMessageTable } from "./autogen/AutogenCarrierDriverMessageTable";
import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { SmsUtility } from "./SmsUtility";
import { FilterPanel } from "@mcleod/dispatch";
import { CarrierDriverMessageTaskFilter } from "./CarrierDriverMessageTaskFilter";

export class CarrierDriverMessageTable extends AutogenLayoutCarrierDriverMessageTable {
    private tooltipRightAlignProps = {
        position: Alignment.RIGHT
    };

    public filterPanel: FilterPanel;

    onLoad(): void | Promise<void> {
        this.filterPanel = new FilterPanel([CarrierDriverMessageTaskFilter.UNREAD, CarrierDriverMessageTaskFilter.PENDING, 
            CarrierDriverMessageTaskFilter.FAILED, CarrierDriverMessageTaskFilter.SCHEDULED], this.tableCarrierDriverMessage, this.panelTaskFilters);
    }

    tableCarrierDriverMessageOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        tableRow.setAdditionalToolProps("pencil", { visible: SmsUtility.showEditButton(modelRow) });
        tableRow.setAdditionalToolProps("delete", { visible: SmsUtility.showDeleteButton(modelRow) });
        tableRow.setAdditionalToolProps("cancelSms", { visible: SmsUtility.showCancelButton(modelRow) });
        tableRow.setAdditionalToolProps("reply", { visible: SmsUtility.showReplyButton(modelRow) });
        tableRow.setAdditionalToolProps("resendMessage", { visible: SmsUtility.showResendButton(modelRow) });
        tableRow.setAdditionalToolProps("markMessageStatus", { visible: SmsUtility.showMarkAsReadUnreadButton(modelRow), tooltip: SmsUtility.setMarkAsRead(modelRow), imageName: SmsUtility.setMarkAsReadImage(modelRow) });
        tableRow.contextMenuItems = () => SmsUtility.getContextMenuItems(tableRow, true);
        SmsUtility.setupDirectionTimeColumn(tableRow, modelRow);
        SmsUtility.setupToFromColumn(tableRow.findComponentById ("labelRecipient") as Label, modelRow);
        SmsUtility.setupStatusColumn(tableRow, modelRow);
        SmsUtility.setupMessageColumn(tableRow.findComponentById("labelMessageText") as Label, modelRow);
        this.setupAttachmentColumn(tableRow, modelRow);
        this.setCarrierTooltipData(tableRow);
        this.setOrderIdTooltipData(tableRow);
        this.setMovementTooltipData(tableRow);
    }


    labelOrderIdOnClick(event: ClickEvent) {
        const orderId = event.target as Label;
        Orders.navigateTo(orderId.text);
    }

    labelMovementOnClick(event: ClickEvent) {
        const movementId = event.target as Label;
        Movement.navigateTo(movementId.text);
    }

    buttonPencilOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow, true, false, this.doAfterAction.bind(this)));
    }

    async buttonDeleteOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await this.processDeleteMessage(modelRow, tableRow);
    }

    async processDeleteMessage(modelRow: any, tableRow: TableRow) {
        const rowId = modelRow.get("parent_row_id");
        if (await CommonDialogs.showYesNo("Are you sure you want to delete this SMS Message?", "Delete SMS Message")) {
            try {
                const response = await Api.post("lme/powerbroker/delete-sms-message", {
                    row_id: rowId
                });
                if (response?.data?.[0]?.message_deleted) {
                    Toast.showSuccessToast("Message has been deleted.", null, null, {millisUntilDismissal: 5000});
                    this.doAfterAction();
                } else {
                    Snackbar.showSnackbar("Failed to delete message.");
                }
            } catch (error) {
                Snackbar.showSnackbar("Failed to delete message.");
            }
        }
    }


    async buttonCancelOnClick(event: ClickEvent) {
        const button = event.target as Button;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await SmsUtility.processCancelMessage(tableRow, modelRow, button, this.doAfterAction.bind(this));
    }

    buttonReplyOnClick(event: ClickEvent, selectedRow: TableRow) {
        const button = event.target as Button;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow, false, true, this.doAfterAction.bind(this)));
    }

    async buttonMarkMessageStatusOnClick(event: ClickEvent) {
        const button = event.target as Button;
        button.busy = true;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        await SmsUtility.updateMessageStatus(tableRow, modelRow);
        button.tooltip = SmsUtility.setMarkAsRead(modelRow);
        button.imageName = SmsUtility.setMarkAsReadImage(modelRow);
        this.doAfterAction();
        button.busy = false;
    }

    buttonResendOnClick(event: ClickEvent){
        const button = event.target as Button;
        const tableRow = this.tableCarrierDriverMessage.findTableRowFromAdditionalTool(button);
        const modelRow = tableRow.data;
        SmsUtility.showSendSmsMessageSlideout(SmsUtility.setLayoutProps(modelRow, false, false, this.doAfterAction.bind(this)));
    }

    doAfterAction() {
        this.mainDataSource.executeValidatedSearch();
    }

    private setupAttachmentColumn(tableRow: TableRow, modelRow: ModelRow<any>) {
        const incomingMessage = StringUtil.equalsIgnoreCase(modelRow.get("direction"),"I");
        if(incomingMessage){
        SmsUtility.setMediaComponents(tableRow, modelRow);
        }
    }

    private setCarrierTooltipData(tableRow: TableRow) {
        const labelCarrierName = tableRow.findComponentById("labelCarrierName") as Label;
        CommonTooltips.setTooltipContentLayout(labelCarrierName, null, "lme/powerbroker/CarrierQuickInfo", {
                minWidth: 100,
                minHeight: 100
            },
            { name: labelCarrierName.text });
        labelCarrierName.tooltipDynamicOptions = this.tooltipRightAlignProps;
    }

    private setMovementTooltipData(tableRow: TableRow) {
        const labelMovement = tableRow.findComponentById ("labelMovement") as Label;
        CommonTooltips.setTooltipContentLayout(labelMovement, null, "lme/powerbroker/MovementQuickInfo", {
                minWidth: 100,
                minHeight: 100
            },
            { id: labelMovement.text });
        labelMovement.tooltipDynamicOptions = this.tooltipRightAlignProps;
    }

    private setOrderIdTooltipData(tableRow: TableRow) {
        const labelOrderId = tableRow.findComponentById ( "labelOrderId") as Label;
        CommonTooltips.setTooltipContentLayout(labelOrderId, null, "lme/powerbroker/OrderQuickInfo", {
                minWidth: 100,
                minHeight: 100
            },
            { id: labelOrderId.text });
        labelOrderId.tooltipDynamicOptions = this.tooltipRightAlignProps;
    }
}